/**
 * User Form Page
 * Felhasználó létrehozása és szerkesztése
 */

import type { UserStatus as ApiUserStatus, UserRole } from '@/api/users';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { useUser, useUserMutations } from '@/hooks/use-users';
import { useTranslation } from '@kgc/i18n';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MOCK_TENANTS } from './mock-data';

const ALL_ROLES: UserRole[] = [
  'OPERATOR',
  'TECHNIKUS',
  'BOLTVEZETO',
  'ACCOUNTANT',
  'PARTNER_OWNER',
  'CENTRAL_ADMIN',
  'DEVOPS_ADMIN',
  'SUPER_ADMIN',
];

interface FormData {
  email: string;
  name: string;
  password: string;
  confirmPassword: string;
  role: UserRole | '';
  status: ApiUserStatus;
  locationId: string;
}

const defaultFormData: FormData = {
  email: '',
  name: '',
  password: '',
  confirmPassword: '',
  role: '',
  status: 'ACTIVE',
  locationId: '',
};

export function UserFormPage() {
  const { t } = useTranslation();

  const STATUS_OPTIONS: { value: ApiUserStatus; label: string }[] = [
    { value: 'ACTIVE', label: t('user.statusLabels.ACTIVE') },
    { value: 'INACTIVE', label: t('user.statusLabels.INACTIVE') },
    { value: 'LOCKED', label: t('user.statusLabels.LOCKED') },
    { value: 'PENDING_VERIFICATION', label: t('user.statusLabels.PENDING_VERIFICATION') },
  ];
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const { user: existingUser, isLoading: isLoadingUser, error: loadError } = useUser(id);
  const {
    createUser,
    updateUser,
    isLoading: isMutating,
    error: mutationError,
  } = useUserMutations();

  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Populate form when existing user loads (needs setState in effect for async data)
  useEffect(() => {
    if (isEditMode && existingUser) {
      setFormData({
        email: existingUser.email,
        name: existingUser.name,
        password: '',
        confirmPassword: '',
        role: existingUser.role,
        status: existingUser.status,
        locationId: existingUser.locationId ?? '',
      });
    }
  }, [isEditMode, existingUser]);

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.email.trim()) {
      newErrors.email = t('user.validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('user.validation.emailInvalid');
    }

    if (!formData.name.trim()) {
      newErrors.name = t('user.validation.nameRequired');
    }

    if (!formData.role) {
      newErrors.role = t('user.validation.roleRequired');
    }

    // Password is required for new users
    if (!isEditMode) {
      if (!formData.password) {
        newErrors.password = t('user.validation.passwordRequired');
      } else if (formData.password.length < 8) {
        newErrors.password = t('user.validation.passwordMinLength');
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t('user.validation.passwordMismatch');
      }
    } else {
      // In edit mode, password is optional but if provided must match
      if (formData.password && formData.password.length < 8) {
        newErrors.password = t('user.validation.passwordMinLength');
      }
      if (formData.password && formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t('user.validation.passwordMismatch');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      if (isEditMode && id) {
        await updateUser(id, {
          name: formData.name,
          email: formData.email,
          role: formData.role as UserRole,
          status: formData.status,
          locationId: formData.locationId || undefined,
        });
        navigate(`/users/${id}`);
      } else {
        await createUser({
          email: formData.email,
          name: formData.name,
          password: formData.password,
          role: formData.role as UserRole,
          locationId: formData.locationId || undefined,
        });
        navigate('/users');
      }
    } catch {
      setSubmitError(mutationError ?? t('user.saveError'));
    }
  };

  // Loading state for edit mode
  if (isEditMode && isLoadingUser) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-[#0f3d47]">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent" />
          <p className="mt-2 text-gray-500 dark:text-[#ccfbf1]">{t('user.loadingUser')}</p>
        </div>
      </div>
    );
  }

  // Error loading existing user
  if (isEditMode && loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-[#0f3d47]">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {t('user.errorOccurred')}
          </h2>
          <p className="text-gray-500 dark:text-[#ccfbf1] mb-4">{loadError}</p>
          <Button onClick={() => navigate('/users')}>{t('common.backToList')}</Button>
        </Card>
      </div>
    );
  }

  if (isEditMode && !existingUser && !isLoadingUser) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-[#0f3d47]">
        <p className="text-gray-500 dark:text-[#ccfbf1]">{t('user.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0f3d47]">
      {/* Header */}
      <header className="bg-white shadow-sm dark:bg-[#1a4f5c]">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate(isEditMode ? `/users/${id}` : '/users')}
            >
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {isEditMode ? t('user.editUser') : t('user.newUserTitle')}
              </h1>
              {isEditMode && existingUser && (
                <p className="text-sm text-gray-500 dark:text-[#99f6e4]">{existingUser.name}</p>
              )}
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            className="bg-primary hover:bg-primary/90"
            disabled={isMutating}
          >
            {isMutating
              ? t('user.savingLabel')
              : isEditMode
                ? t('common.save')
                : t('user.createLabel')}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Submit error */}
        {(submitError ?? mutationError) && (
          <Card className="mb-6 border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
            <CardContent className="pt-4">
              <p className="text-sm text-red-600 dark:text-red-400">
                {submitError ?? mutationError}
              </p>
            </CardContent>
          </Card>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>{t('user.basicData')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {t('user.nameRequired')}
                  </label>
                  <Input
                    value={formData.name}
                    onChange={e => updateField('name', e.target.value)}
                    placeholder={t('user.namePlaceholder')}
                    className={errors.name ? 'border-red-500' : ''}
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {t('user.emailRequired')}
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={e => updateField('email', e.target.value)}
                    placeholder={t('user.emailPlaceholder')}
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Password */}
          <Card>
            <CardHeader>
              <CardTitle>{isEditMode ? t('user.passwordChange') : t('login.password')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {isEditMode ? t('user.newPassword') : t('user.passwordRequired')}
                  </label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={e => updateField('password', e.target.value)}
                    placeholder={t('user.minChars')}
                    className={errors.password ? 'border-red-500' : ''}
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                  )}
                  {isEditMode && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-[#ccfbf1]">
                      {t('user.passwordLeaveEmpty')}
                    </p>
                  )}
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {isEditMode ? t('user.confirmPassword') : t('user.confirmPasswordRequired')}
                  </label>
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={e => updateField('confirmPassword', e.target.value)}
                    placeholder={t('user.passwordAgain')}
                    className={errors.confirmPassword ? 'border-red-500' : ''}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Role & Tenant */}
          <Card>
            <CardHeader>
              <CardTitle>{t('user.roleAndLocation')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {t('user.roleRequired')}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {ALL_ROLES.map(role => (
                      <button
                        key={role}
                        type="button"
                        onClick={() => updateField('role', role)}
                        className={`rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                          formData.role === role
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-[#1f5f6b] dark:text-[#ccfbf1] dark:hover:bg-[#0f3d47]'
                        }`}
                      >
                        {t(`user.roleLabels.${role}`)}
                      </button>
                    ))}
                  </div>
                  {errors.role && <p className="mt-1 text-sm text-red-500">{errors.role}</p>}
                </div>

                {isEditMode && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                      {t('table.status')}
                    </label>
                    <select
                      value={formData.status}
                      onChange={e => updateField('status', e.target.value as ApiUserStatus)}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-[#2d7d8a] dark:bg-[#1a4f5c] dark:text-white"
                    >
                      {STATUS_OPTIONS.map(s => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-[#ccfbf1]">
                    {t('user.locationOptional')}
                  </label>
                  <select
                    value={formData.locationId}
                    onChange={e => updateField('locationId', e.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-[#2d7d8a] dark:bg-[#1a4f5c] dark:text-white"
                  >
                    <option value="">{t('user.notSpecified')}</option>
                    {MOCK_TENANTS.map(tenant => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(isEditMode ? `/users/${id}` : '/users')}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" className="bg-primary hover:bg-primary/90" disabled={isMutating}>
              {isMutating
                ? t('user.savingLabel')
                : isEditMode
                  ? t('user.saveChanges')
                  : t('user.createUser')}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
